import {
    patchAccountPreferences,
    patchAccountFeatureFlag,
    type AccountPreferencesPatch,
} from "@common/lib/api/accountPreferences";
import {
    Account,
    AccountIntegration,
    Communications,
    FeatureFlags,
    LLMProviders,
    PIMSDisplay,
    PIMSType,
} from "@common/utils/types";
import type { User } from "@supabase/supabase-js";
import { useSession } from "@supabase/auth-helpers-react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { hydrateFromStorage } from "@/state/redux/resourcesSlice";
import * as Sentry from "@sentry/react";
import {
    AccountAccessDeniedError,
    fetchAccountData,
    fetchCommunicationsData,
    updateAccountPreferredProvider,
    updateExportEnabled,
    updateCornerstoneDefaultAlertColor,
} from "@common/lib/supabaseClient";
import { signOutLocal } from "@common/lib/signOut";
import { createCoalescingIdleRunner, type CoalescingIdleRunner } from "@common/lib/recorderActivity";
import {
    getStoredSelectedClinicAccountId,
    setStoredSelectedClinicAccountId,
} from "@common/lib/selectedClinicAccountStorage";
import { changePIMS } from "@/common/lib/api";
import { IntegrationProvider } from "@/features/settings/components/integrations/Integrations";
import { useImpersonation } from "./ImpersonationProvider";
import { pushToDataLayer } from "@common/utils/dataLayer";
import { toast } from "react-toastify";
import { ENDPOINTS } from "@/common/utils/url-registry";
import { useQueryClient } from "@tanstack/react-query";
import { accountIntegrationsQueryKey, useAccountIntegrationsQuery } from "@/state/hooks/useAccountIntegrationsQuery";

/**
 * Provides a context for managing and accessing account information throughout the application.
 */
export const AccountContext = createContext<
    | {
          accountData: Account | undefined;
          setAccountData: React.Dispatch<React.SetStateAction<Account | {}>>;
          communicationsData: Communications[] | undefined;
          setCommunicationsData: React.Dispatch<React.SetStateAction<Communications[] | undefined>>;
          pmsIntegrationData: AccountIntegration[] | null;
          updateIntegrationExportEnabled: (integrationId: string, exportEnabled: boolean) => Promise<void>;
          updateIntegrationDefaultAlertColor: (integrationId: string, color: string | null) => Promise<boolean>;
          containsFeatureFlag: (featureKey: string) => boolean;
          dataLoaded: boolean;
          handlePimsTypeChange: (selectedDisplayName: string) => Promise<void>;
          updateIsMarkDownEnabled: (newEnabled: boolean) => Promise<void>;
          updatePreferences: (patch: AccountPreferencesPatch) => Promise<void>;
          updateFeatureFlag: (featureFlag: FeatureFlags, enabled: boolean) => Promise<void>;
          fileIntegrationEnabled: () => boolean;
          hasActiveIntegration: () => boolean;
          changeFeatureFlag: (featureFlag: FeatureFlags, add: boolean) => void;
          updatePreferredProvider: (preferredProvider: LLMProviders) => Promise<void>;
          refetchAccountData: () => Promise<void>;
      }
    | undefined
>(undefined);

// PIMS types that have copy in markdown enabled by default
const defaultCopyInMarkdownEnabled = [PIMSType.EZYVET];

/** Supabase sometimes omits `user.email` briefly after password / invite flows; metadata still has it. */
function resolveAuthUserEmail(user: User): string | undefined {
    const direct = user.email?.trim();
    if (direct) return direct;
    const meta = user.user_metadata;
    if (meta && typeof meta.email === "string" && meta.email.trim()) {
        return meta.email.trim();
    }
    const pending = (user as { new_email?: string | null }).new_email;
    if (typeof pending === "string" && pending.trim()) {
        return pending.trim();
    }
    return undefined;
}

/**
 * EN-1313: the single "no account" value exposed while the stored row belongs to a different identity
 * (or none has loaded). One shared object, not a fresh `{}` per render, so effects keyed on
 * `accountData` don't re-run and clean up on every provider render while masked. Frozen because the
 * same object is exposed for every identity while masked, so an in-place write would carry into the next
 * identity's view; it throws instead.
 */
const EMPTY_ACCOUNT: Account | {} = Object.freeze({});

/**
 * The identity tag for the account snapshot. The render-time mask, fetchData and refetchAccountData
 * must all build it the same way, or the mask never lifts (spinner) or never applies.
 */
const accountIdentityKey = (sessionUserId: string, effectiveUserId: string) => `${sessionUserId}:${effectiveUserId}`;

/**
 * Account row + communications, tagged with the identity they were fetched for. `key` is
 * `accountIdentityKey(...)` (null = settled with no session, nothing to fetch; undefined = not settled yet).
 */
type AccountSnapshot = {
    key: string | null | undefined;
    data: Account | {};
    communications: Communications[] | undefined;
};

/**
 * The AccountProvider component fetches and provides account, doctor, PMS integration,
 * and subscription data for children components. It manages state related to the current user's
 * session and data, initializing with session check and data fetching, and provides functions
 * to manipulate this data.
 * @param {React.ReactNode} children - The children components that will consume the context.
 */
export const AccountProvider = ({ children }: { children: React.ReactNode }) => {
    // Initialize the session using Supabase's useSession hook for user authentication.
    const session = useSession();
    const { impersonationSession, isImpersonating } = useImpersonation();
    const dispatch = useDispatch();

    // State management
    const [snapshot, setSnapshot] = useState<AccountSnapshot>({
        key: undefined,
        data: EMPTY_ACCOUNT,
        communications: undefined,
    });

    // EN-1313: the identity every request is sent as changes the moment impersonation starts or stops
    // (apiFetch reads it from localStorage at send time), but the row for the new identity takes a round
    // trip to load. Until it does, expose nothing rather than the previous identity's row: consumers
    // that read `accountData.id` would otherwise pair the new identity with the old clinic id (401s,
    // and direct reads scoped to the wrong clinic). Computed during render, not in an effect, because
    // child effects run before this provider's fetch effect, so even one stale commit fires requests.
    const effectiveUserId = session?.user
        ? isImpersonating && impersonationSession?.targetUserId
            ? impersonationSession.targetUserId
            : session.user.id
        : undefined;
    const identityKey = session?.user && effectiveUserId ? accountIdentityKey(session.user.id, effectiveUserId) : null;
    const identityMatches = snapshot.key === identityKey;
    // Last rendered identity (assigned during render), read by refetchAccountData after its await to drop a
    // response whose identity has since changed. It lags a same-tab start/stop until the next render.
    const identityKeyRef = useRef(identityKey);
    identityKeyRef.current = identityKey;
    const accountData = identityMatches ? snapshot.data : EMPTY_ACCOUNT;
    const communicationsData = identityMatches ? snapshot.communications : undefined;
    // Loaded means "a fetch has settled for THIS identity".
    const dataLoaded = identityMatches;

    // Consumer and internal updaters edit the stored row and never touch its identity tag, so an edit
    // made while masked stays hidden and is replaced by the new identity's row when it lands.
    const setAccountData = useCallback<React.Dispatch<React.SetStateAction<Account | {}>>>((action) => {
        setSnapshot((prev) => ({ ...prev, data: typeof action === "function" ? action(prev.data) : action }));
    }, []);
    const setCommunicationsData = useCallback<React.Dispatch<React.SetStateAction<Communications[] | undefined>>>(
        (action) => {
            setSnapshot((prev) => ({
                ...prev,
                communications: typeof action === "function" ? action(prev.communications) : action,
            }));
        },
        [],
    );
    const abortControllerRef = useRef<AbortController | null>(null);
    const isMountedRef = useRef(true);
    const refetchAbortControllerRef = useRef<AbortController | null>(null);
    // Tracks whether we've ever resolved a valid clinic account ID in this session.
    // Used to distinguish the initial load (accountData not yet fetched → clinicAccountId undefined)
    // from an impersonation switch (clinicAccountId briefly undefined between accounts).
    // We must not clear localStorage on the initial load — that would erase stored selections
    // before hydrateFromStorage has a chance to read them.
    const hadAccountIdRef = useRef(false);

    // EN-837: single-slot scheduler for the "team sign-in disabled" teardown (sign-out + hard-nav),
    // deferred until the recorder is idle. Account-data (re)fetch re-runs on every session-object
    // identity change (token refresh, ~hourly) and impersonation change; without coalescing, each
    // failed fetch during one recording would stack a separate deferred sign-out (and leak a store
    // subscription), all firing when the recorder goes idle. This runner holds at most one.
    // Lazy-init so the factory runs once, not on every render (useRef evaluates its arg each render
    // and discards all but the first — a wasted allocation per render).
    const accessDeniedSignOutRunnerRef = useRef<CoalescingIdleRunner | null>(null);
    if (!accessDeniedSignOutRunnerRef.current) {
        accessDeniedSignOutRunnerRef.current = createCoalescingIdleRunner();
    }
    const scheduleAccountAccessSignOut = useCallback(() => {
        // Navigate only AFTER signOutLocal resolves: supabase.auth.signOut clears the stored token as
        // its LAST step (behind a network call), and window.location.assign is a hard nav that can abort
        // that in-flight work — leaving a valid token in storage that bounces the user right back in.
        // signOutLocal swallows its own errors and never rejects, so .finally always navigates.
        accessDeniedSignOutRunnerRef.current?.schedule(() => {
            void signOutLocal().finally(() => window.location.assign(`/${ENDPOINTS.Login}`));
        });
    }, []);

    // Track mount state so refetchAccountData doesn't call setState after unmount
    useEffect(() => {
        isMountedRef.current = true;
        const accessDeniedSignOutRunner = accessDeniedSignOutRunnerRef.current;
        return () => {
            isMountedRef.current = false;
            // Abort any in-flight refetch so it doesn't complete and try to set state
            if (refetchAbortControllerRef.current) {
                refetchAbortControllerRef.current.abort();
                refetchAbortControllerRef.current = null;
            }
            // Cancel a still-deferred sign-out so it can't fire (signOut + hard-nav) after unmount.
            accessDeniedSignOutRunner?.cancel();
        };
    }, []);

    // Derive clinicAccountId so the hydration effect has a stable primitive dependency.
    const clinicAccountId =
        accountData && typeof accountData === "object" && "id" in accountData && (accountData as Account).id
            ? String((accountData as Account).id)
            : undefined;

    // account_integrations is scoped to the resolved clinic account id (accountData.id), not the
    // auth user id — required for team members where account.id != user.id (see fetchData above).
    // Falls back to the stored selection / effective user id so a transient failure of the
    // *account* fetch doesn't also block the integrations read that would otherwise recover fine —
    // narrowing this to `clinicAccountId` alone would regress that resilience (EN-1137).
    //
    // The raw-user-id leg is gated on `dataLoaded`, and that gate is load-bearing. This value is
    // derived during render, so it resolves BEFORE `fetchData` has produced `accountData` (hence
    // `clinicAccountId`). For a team member on a fresh browser — no stored selection yet — the
    // ungated version's first query ran against the auth user id, which is not an account id for
    // them. That is not an error the query layer can see: it is a successful read returning a
    // genuine `[]` for a scope nobody asked about, which downstream reads as "confirmed: this
    // clinic has no integration" (not "still unknown") and sits in the cache under that key for
    // its whole `gcTime`. The pre-EN-1137 code never hit this because it only ever computed the
    // scope inside `fetchData`, after the account row had already resolved.
    //
    // The *stored* selection is not gated: it was written from a previously resolved
    // `accountRow.id` (see fetchData below), so it is already the right scope and gives a warm
    // browser its integrations without waiting on the account fetch. Once `dataLoaded` is true the
    // full chain applies again whether the account fetch SUCCEEDED or FAILED (fetchData's `finally`
    // settles the snapshot for the current identity either way), so a failed account fetch still
    // doesn't block this read.
    const integrationsAccountId =
        clinicAccountId ??
        (effectiveUserId
            ? (getStoredSelectedClinicAccountId(effectiveUserId) ?? (dataLoaded ? effectiveUserId : undefined))
            : undefined);

    const integrationsQuery = useAccountIntegrationsQuery(integrationsAccountId);
    const pmsIntegrationData = integrationsQuery.data ?? null;

    const queryClient = useQueryClient();

    // EN-1137: the old code silently swallowed a failed integrations read into "no integration".
    // Report it once per continuous outage (not once per 30s retry) so it's finally visible outside
    // this component — the ticket's whole point was that this class of failure left no trace anywhere.
    const integrationsErrorReportedRef = useRef(false);
    useEffect(() => {
        if (integrationsQuery.isError && !integrationsErrorReportedRef.current) {
            integrationsErrorReportedRef.current = true;
            Sentry.captureException(integrationsQuery.error, {
                tags: { errorCategory: "integrations", operation: "getAccountIntegrationsStrict" },
            });
        } else if (!integrationsQuery.isError) {
            integrationsErrorReportedRef.current = false;
        }
    }, [integrationsQuery.isError, integrationsQuery.error]);

    // Hydrate resource selections from localStorage once the accountId is resolved.
    // Deferred hydration ensures we validate stored accountId against the current clinic.
    //
    // During impersonation switches clinicAccountId passes through undefined between accounts.
    // We clear localStorage synchronously on that transition because the middleware listener
    // that handles clearResourceState is async (RTK listener effects run in a microtask) and
    // may not have flushed before clinicAccountId resolves to the new account ID, creating a
    // window where hydrateFromStorage reads stale data from the previous account's selections.
    useEffect(() => {
        if (!clinicAccountId) {
            // Only clear persisted selections when transitioning away from a known account
            // (impersonation switch). On the initial render accountData hasn't loaded yet, so
            // clinicAccountId is undefined — clearing here would erase stored selections before
            // hydrateFromStorage can read them.
            if (hadAccountIdRef.current) {
                localStorage.removeItem("selectedResource");
                localStorage.removeItem("selectedResources");
            }
            return;
        }
        hadAccountIdRef.current = true;
        dispatch(hydrateFromStorage({ accountId: clinicAccountId }));
    }, [clinicAccountId, dispatch]);

    // Utility function to abort previous requests
    const abortPreviousRequest = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    };

    // Utility function to clean up the AbortController reference
    const cleanUpController = (controller: AbortController) => {
        if (abortControllerRef.current === controller) {
            abortControllerRef.current = null;
        }
    };

    // function to check if the account has the File integration enabled
    const fileIntegrationEnabled = useCallback(() => {
        // if there is no integration data, return true
        if (!pmsIntegrationData) return true;

        // if there are no integrations, return true
        if (pmsIntegrationData.length === 0) return true;

        // check if the account any active integrations
        const activeIntegrations = pmsIntegrationData.filter((integration) => integration.is_active);

        // check if the account has the file integration enabled
        const hasFileIntegration = activeIntegrations.some(
            (integration) =>
                integration?.integrations?.integration_name === IntegrationProvider.File && integration.is_active,
        );

        // check if the account has only the file integration enabled
        const otherActiveIntegrations = activeIntegrations.filter(
            (integration) => integration?.integrations?.integration_name !== IntegrationProvider.File,
        );

        // return true if the account has the file integration enabled and there are no other active integrations
        return hasFileIntegration && otherActiveIntegrations.length === 0;
    }, [pmsIntegrationData]);

    // function to check if the account has any active integrations
    const hasActiveIntegration = useCallback(() => {
        if (!pmsIntegrationData) return false;

        if (pmsIntegrationData.length === 0) return false;

        return pmsIntegrationData.some((integration) => integration.is_active);
    }, [pmsIntegrationData]);

    useEffect(() => {
        // Fetches account data, subscription, and PMS integration data.
        const fetchData = async () => {
            if (!session?.user) {
                abortPreviousRequest();
                setSnapshot({ key: null, data: EMPTY_ACCOUNT, communications: undefined });
                return;
            }

            // Determine which user ID to use for data fetching
            const effectiveUserId =
                isImpersonating && impersonationSession?.targetUserId
                    ? impersonationSession.targetUserId
                    : session.user.id;

            // The snapshot is tagged with the identity this fetch was issued for, and exposed only while
            // that is still the current identity.
            const fetchKey = accountIdentityKey(session.user.id, effectiveUserId);

            const resolvedEmail = resolveAuthUserEmail(session.user);

            // Set the current user in Sentry for error tracking (always use admin user for Sentry)
            Sentry.setUser({ id: session.user.id, email: resolvedEmail ?? session.user.id });

            // Abort any previous fetch requests.
            abortPreviousRequest();

            const controller = new AbortController();
            abortControllerRef.current = controller;

            try {
                // Resolve clinic account first so Supabase queries on account-scoped tables use clinic id
                // (account_id on rows), not auth user id — required for team members (account.id ≠ user.id).
                const accountRow = await fetchAccountData(controller.signal, effectiveUserId);

                if (controller.signal.aborted) {
                    return;
                }

                const clinicAccountIdForScopedQueries =
                    (accountRow?.id && String(accountRow.id)) ||
                    getStoredSelectedClinicAccountId(effectiveUserId) ||
                    effectiveUserId;

                const communicationsData = await fetchCommunicationsData(
                    controller.signal,
                    clinicAccountIdForScopedQueries,
                );

                if (controller.signal.aborted) {
                    return;
                }

                // Set fetched data in state
                // Use the effective user's email for display
                const effectiveUserEmail =
                    isImpersonating && impersonationSession?.targetEmail
                        ? impersonationSession.targetEmail
                        : (resolvedEmail ?? "");

                // This fetch completed WITHOUT throwing account_users_disabled (which is thrown and caught
                // below), so access isn't (confirmed) denied — drop any sign-out still deferred from an
                // earlier denied fetch so it can't fire a stale teardown once the recorder goes idle. Do
                // this for BOTH a real row AND a transient null (fetchAccountData returns null on error):
                // a network blip after access was re-enabled mid-recording must not sign the user out at
                // idle. A genuinely disabled account re-throws and re-arms on the next fetch. (EN-837 / Bugbot)
                accessDeniedSignOutRunnerRef.current?.cancel();

                // fetchAccountData returns null on error. Record that as "loaded, no account" for this
                // identity rather than skipping the write, so a same-identity re-run of this effect (session
                // object churn) that comes back null does not keep showing a row the server no longer returns.
                // refetchAccountData keeps the row on null instead.
                setSnapshot({
                    key: fetchKey,
                    data: accountRow != null ? { ...accountRow, auth_email: effectiveUserEmail } : EMPTY_ACCOUNT,
                    communications: communicationsData || undefined,
                });
                if (accountRow?.id) {
                    setStoredSelectedClinicAccountId(effectiveUserId, String(accountRow.id));
                }

                // Push user data to dataLayer for GTM tracking (always send both dlv_user_id and dlv_user together)
                if (accountRow && effectiveUserId) {
                    pushToDataLayer({
                        dlv_user_id: effectiveUserId,
                        dlv_user: {
                            clinic_name: accountRow.clinic_name,
                            clinic_location: accountRow.clinic_location,
                            email: effectiveUserEmail,
                            created_at: accountRow.created_at,
                        },
                    });
                }
            } catch (error: any) {
                if (error instanceof AccountAccessDeniedError && error.reason === "account_users_disabled") {
                    toast.error("Team sign-in is disabled for this clinic. Contact your clinic owner.");
                    // Defer the destructive teardown (sign-out + hard-nav) until the recorder is idle so we
                    // can't tear down an in-flight recording/upload mid-capture (EN-837). Runs immediately if
                    // idle; coalesced so repeated fetches during one recording don't stack sign-outs.
                    scheduleAccountAccessSignOut();
                    return;
                }
                Sentry.captureException(error, {
                    tags: { errorCategory: "auth", operation: "fetchAccountData" },
                });
            } finally {
                // Always unblock the app gate. Aborted runs skip setState above so we do not commit stale rows; never
                // settling here caused infinite spinners after updateUser (session churn aborts in-flight fetch).
                // A run that threw for the SAME identity (e.g. after a token refresh) keeps the row it already has.
                // A run that threw for a NEW identity settles to "loaded, no account" rather than spinning forever
                // or re-exposing the previous identity's row (EN-1313).
                if (!controller.signal.aborted) {
                    setSnapshot((prev) =>
                        prev.key === fetchKey
                            ? prev
                            : { key: fetchKey, data: EMPTY_ACCOUNT, communications: undefined },
                    );
                }

                cleanUpController(controller);
            }
        };

        fetchData();

        return () => {
            abortPreviousRequest();
        };
    }, [session, isImpersonating, impersonationSession]);

    // Re-fetch account data on demand (e.g. after Stripe checkout updates account status)
    const refetchAccountData = useCallback(async () => {
        if (!session?.user?.id) return;
        // Abort any previous refetch so we don't have multiple in flight
        if (refetchAbortControllerRef.current) {
            refetchAbortControllerRef.current.abort();
        }
        const controller = new AbortController();
        refetchAbortControllerRef.current = controller;
        const effectiveUserId =
            isImpersonating && impersonationSession?.targetUserId ? impersonationSession.targetUserId : session.user.id;
        const refetchKey = accountIdentityKey(session.user.id, effectiveUserId);
        try {
            const data = await fetchAccountData(controller.signal, effectiveUserId, true);
            // Only update state if still mounted (e.g. user didn't log out before fetch completed)
            if (!isMountedRef.current) return;
            // Completed without throwing account_users_disabled → access isn't (confirmed) denied. Drop any
            // stale deferred sign-out for BOTH a real row and a transient null, so a network blip after
            // access was restored can't sign the user out at recorder-idle. A disabled account re-throws
            // and re-arms next time. (EN-837 / Bugbot)
            accessDeniedSignOutRunnerRef.current?.cancel();
            if (data == null) return; // fetchAccountData returns null on error; avoid wiping accountData
            const effectiveUserEmail =
                isImpersonating && impersonationSession?.targetEmail
                    ? impersonationSession.targetEmail
                    : (resolveAuthUserEmail(session.user) ?? "");
            // EN-1313: apply only if the identity is the same now as when this refetch was issued. A refetch
            // still in flight across an impersonation start/stop would otherwise write the old identity's row
            // back, or store the other identity's clinic as the selected clinic of the one it was issued for.
            // A switch and switch back within one refetch is not detected.
            if (identityKeyRef.current !== refetchKey) return;
            setSnapshot((prev) =>
                prev.key === refetchKey ? { ...prev, data: { ...data, auth_email: effectiveUserEmail } } : prev,
            );
            if (data.id) {
                setStoredSelectedClinicAccountId(effectiveUserId, String(data.id));
            }
        } catch (err) {
            // AbortError is expected when provider unmounts; ignore. Other errors: stale data is acceptable.
            if (err instanceof DOMException && err.name === "AbortError") return;
            if (err instanceof AccountAccessDeniedError && err.reason === "account_users_disabled") {
                toast.error("Team sign-in is disabled for this clinic. Contact your clinic owner.");
                // Defer the destructive teardown (sign-out + hard-nav) until the recorder is idle so we
                // can't tear down an in-flight recording/upload mid-capture (EN-837). Runs immediately if
                // idle; coalesced with the fetchData branch so repeated fetches don't stack sign-outs.
                scheduleAccountAccessSignOut();
                return;
            }
            // Stale accountData is an acceptable user-facing fallback (next navigation refreshes), but the
            // error signal is NOT disposable: an unexpected throw here (shape change, a different
            // AccountAccessDeniedError.reason, a regression) would otherwise be invisible. Report it, then
            // keep the stale-data fallback. Mirrors the fetchData catch. (EN-837)
            Sentry.captureException(err, {
                tags: { errorCategory: "auth", operation: "refetchAccountData" },
            });
        } finally {
            if (refetchAbortControllerRef.current === controller) {
                refetchAbortControllerRef.current = null;
            }
        }
    }, [session, isImpersonating, impersonationSession]);

    // Utility function to check if the account has a specific feature flag enabled.
    const containsFeatureFlag = useCallback(
        (featureKey: string) => {
            if (typeof accountData === "object" && accountData !== null && "feature_flags" in accountData) {
                const flags = accountData.feature_flags;
                return Array.isArray(flags) && flags.includes(featureKey);
            }
            return false;
        },
        [accountData],
    );

    /**
     * Persist clinic-wide preferences (EN-1075).
     *
     * Goes through API-Server, which resolves the clinic from the session. The previous
     * direct-Supabase writers keyed the update off `session.user.id`, so they wrote nothing for a
     * non-owner member and wrote to the admin's own account row while impersonating.
     *
     * The response is the server's merged blob, so provider state ends up matching the row rather
     * than a locally-guessed merge - which matters because `meta_data` has other writers.
     * Throws on failure so callers can surface an error instead of the toggle silently reverting.
     */
    const updatePreferences = async (patch: AccountPreferencesPatch) => {
        const merged = await patchAccountPreferences(patch);
        setAccountData((prev: Account) => ({ ...prev, meta_data: merged }));
    };

    /**
     * Update copy in markdown configuration
     * @returns void
     */
    const updateIsMarkDownEnabled = async (newEnabled: boolean) => {
        await updatePreferences({ markdown_copy_enabled: newEnabled });
    };

    /**
     * Toggle an experimental feature flag and persist it (EN-1075).
     *
     * `changeFeatureFlag` below only mutates provider state - it does not write - so a UI toggle
     * built on it looked like it worked and reverted on reload. This writes first, then adopts the
     * server's flag list. Owner-only server-side; throws on 403 so the caller can say so.
     */
    const updateFeatureFlag = async (featureFlag: FeatureFlags, enabled: boolean) => {
        const flags = await patchAccountFeatureFlag(featureFlag, enabled);
        setAccountData((prev: Account) => ({ ...prev, feature_flags: flags }));
    };

    /**
     * Update the export enabled status for an integration
     * @param integrationId - The ID of the integration to update
     * @param exportEnabled - The new export enabled status
     * @returns void
     */
    const updateIntegrationExportEnabled = async (integrationId: string, exportEnabled: boolean) => {
        // check for session
        if (!session?.user?.id) {
            return;
        }

        // update the db
        const { error } = await updateExportEnabled(integrationId, exportEnabled);
        if (error) {
            return;
        }

        // update the query cache
        queryClient.setQueryData<AccountIntegration[]>(accountIntegrationsQueryKey(integrationsAccountId), (prev) => {
            if (!prev) return prev;
            return prev.map((integration) =>
                integration.id === integrationId ? { ...integration, export_enabled: exportEnabled } : integration,
            );
        });
    };

    /**
     * Update the account-level default Cornerstone record color
     * @param integrationId - The account_integrations row id
     * @param color - "#RRGGBB", or null to clear the default
     * @returns true on success, false if the DB update failed (no session counts as failure)
     */
    const updateIntegrationDefaultAlertColor = async (
        integrationId: string,
        color: string | null,
    ): Promise<boolean> => {
        if (!session?.user?.id) {
            return false;
        }

        const { error } = await updateCornerstoneDefaultAlertColor(integrationId, color);
        if (error) {
            return false;
        }

        queryClient.setQueryData<AccountIntegration[]>(accountIntegrationsQueryKey(integrationsAccountId), (prev) => {
            if (!prev) return prev;
            return prev.map((integration) =>
                integration.id === integrationId
                    ? { ...integration, cornerstone_default_alert_color: color }
                    : integration,
            );
        });

        return true;
    };

    /**
     * Update the preferred provider for the account
     * @param preferredProvider - The preferred provider to update
     * @returns void
     */
    const updatePreferredProvider = async (preferredProvider: LLMProviders) => {
        if (!session?.user?.id) {
            return;
        }

        // update the db
        const { error } = await updateAccountPreferredProvider({
            id: session.user.id,
            preferred_provider: preferredProvider,
        });
        if (error) {
            return;
        }

        // set provider state
        setAccountData((prev: Account) => ({
            ...prev,
            preferred_provider: preferredProvider,
        }));
    };

    /**
     * Handle PIMS type change
     * @param selectedDisplayName  selected PIMS type
     * @returns void
     * */
    const handlePimsTypeChange = async (selectedDisplayName: string) => {
        if (!session?.user?.id || !accountData) {
            Sentry.captureException(new Error("User ID or account data not found"));
            return;
        }

        // find the selected pims type
        const selectedPimsType = Object.keys(PIMSDisplay).find(
            (key) => PIMSDisplay[key as PIMSType] === selectedDisplayName,
        ) as PIMSType | undefined;

        // @ts-expect-error accountData is typed as {} | Account, so current_pims is not statically known here
        if (!selectedPimsType || accountData?.current_pims === selectedPimsType) {
            return;
        }

        // now update the db state
        const { error } = await changePIMS(selectedPimsType);

        // handle error
        if (error) {
            Sentry.captureException(error);
            return;
        }

        // set provider state
        setAccountData((prev: Account) => ({
            ...prev,
            current_pims: selectedPimsType,
            meta_data: {
                ...prev.meta_data,
                markdown_copy_enabled: defaultCopyInMarkdownEnabled.includes(selectedPimsType),
            },
        }));

        return;
    };

    /**
     * Updates the feature flags for the account data.
     *
     * @param {FeatureFlags} featureFlag - The feature flag to be added or removed.
     * @param {boolean} add - A boolean indicating whether to add or remove the feature flag.
     *
     * If `add` is true, the feature flag will be added to the account's feature flags.
     * If `add` is false, the feature flag will be removed from the account's feature flags.
     *
     * If `accountData` is not available, the function will return early without making any changes.
     */
    const changeFeatureFlag = useCallback(
        (featureFlag: FeatureFlags, add: boolean) => {
            if (add) {
                setAccountData((prev: Account) => ({
                    ...prev,
                    feature_flags: prev?.feature_flags ? [...prev.feature_flags, featureFlag] : [featureFlag],
                }));
            } else {
                setAccountData((prev: Account) => ({
                    ...prev,
                    feature_flags: prev?.feature_flags?.filter((flag) => flag !== featureFlag),
                }));
            }
        },
        [accountData],
    );

    return (
        <AccountContext.Provider
            value={{
                accountData,
                setAccountData,
                communicationsData,
                setCommunicationsData,
                pmsIntegrationData,
                updateIntegrationExportEnabled,
                updateIntegrationDefaultAlertColor,
                containsFeatureFlag,
                dataLoaded,
                handlePimsTypeChange,
                updateIsMarkDownEnabled,
                updatePreferences,
                updateFeatureFlag,
                fileIntegrationEnabled,
                hasActiveIntegration,
                changeFeatureFlag,
                updatePreferredProvider,
                refetchAccountData,
            }}
        >
            {children}
        </AccountContext.Provider>
    );
};

/**
 * Custom hook to consume account context. It ensures the context is used within its provider.
 * Throws an error if used outside the `AccountProvider`.
 * @returns The account context with user's account data, doctors, PMS integration data, subscription info, and utility functions.
 */
export const useAccountContext = () => {
    const context = useContext(AccountContext);
    if (context === undefined) {
        throw new Error("useAccountContext must be used within a AccountProvider");
    }
    return context;
};
